import React, { useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Table,
  ButtonGroup,
  Button
} from "reactstrap";
import { buildMarkerWorkloadRows } from "utils/markerWorkloadRows";

function DashboardStatSkeleton({ label }) {
  return (
    <span className="d-inline-flex flex-column" aria-busy="true">
      <span className="sr-only">Loading {label}</span>
      <span className="dashboard-stat-skeleton__value" aria-hidden="true" />
    </span>
  );
}

function fmtNum(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return Number(v).toLocaleString();
}

export default function AdminMarkerWorkloadSection({
  funnel,
  funnelLoading,
  statsYear,
  statsLoading
}) {
  const [sortKey, setSortKey] = useState("pending");
  const [sortDir, setSortDir] = useState("desc");

  const yearLabel =
    funnel && funnel.year != null ? funnel.year : statsYear;

  const rows = useMemo(() => buildMarkerWorkloadRows(funnel), [funnel]);

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((x, y) => {
      let cmp = 0;
      if (sortKey === "code") {
        cmp = String(x.code).localeCompare(String(y.code), undefined, {
          sensitivity: "base"
        });
      } else if (sortKey === "firstName") {
        cmp = String(x.firstName).localeCompare(String(y.firstName), undefined, {
          sensitivity: "base"
        });
      } else if (sortKey === "lastName") {
        cmp = String(x.lastName).localeCompare(String(y.lastName), undefined, {
          sensitivity: "base"
        });
      } else if (sortKey === "assigned") {
        cmp = x.assigned - y.assigned;
      } else if (sortKey === "marked") {
        cmp = x.marked - y.marked;
      } else {
        cmp = x.pending - y.pending;
      }
      return cmp * dir;
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  const toggleSort = key => {
    if (sortKey === key) {
      setSortDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(
        key === "code" || key === "firstName" || key === "lastName"
          ? "asc"
          : "desc"
      );
    }
  };

  const sortIndicator = key =>
    sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const showLoading = funnelLoading && !funnel;
  const empty =
    !showLoading &&
    funnel &&
    Array.isArray(funnel.by_marker) &&
    funnel.by_marker.length === 0;
  const noFunnelYet = !showLoading && !funnel;

  if (statsLoading.assessed || statsLoading.available) return null;

  return (
    <Row className="mt-2">
      <Col xs="12">
        <Card className="card-stats mb-4 mb-xl-0 shadow-sm">
          <CardBody>
            <CardTitle tag="h5" className="text-uppercase text-muted mb-1">
              Markers
            </CardTitle>
            <p className="small text-muted mb-3">
              Assigned, marked, and pending counts for reporting year{" "}
              <strong>{yearLabel}</strong>. Loaded from{" "}
              <code className="text-dark">
                GET /api/marker-portal-dashboard-funnel?year=
              </code>{" "}
              (JSON field <span className="font-italic">by_marker</span>). For
              name columns, the API can send{" "}
              <code className="text-dark">first_name</code> /{" "}
              <code className="text-dark">last_name</code> (or{" "}
              <code className="text-dark">full_name</code>).
            </p>

            {showLoading ? (
              <div className="py-3">
                <DashboardStatSkeleton label="marker workload" />
              </div>
            ) : empty || noFunnelYet ? (
              <p className="small text-muted mb-0">
                {noFunnelYet
                  ? "Could not load funnel data for this year. Check the admin API and CORS."
                  : "No per-marker rows returned for this year. Ensure Laravel populates by_marker (marker_code, pending, marked, optional first_name / last_name)."}
              </p>
            ) : (
              <>
                <div className="d-flex flex-wrap align-items-center mb-2">
                  <span className="small text-muted mr-2">Sort by:</span>
                  <ButtonGroup size="sm" className="flex-wrap">
                    <Button
                      outline
                      color="secondary"
                      active={sortKey === "code"}
                      onClick={() => toggleSort("code")}
                    >
                      Code{sortIndicator("code")}
                    </Button>
                    <Button
                      outline
                      color="secondary"
                      active={sortKey === "firstName"}
                      onClick={() => toggleSort("firstName")}
                    >
                      First name{sortIndicator("firstName")}
                    </Button>
                    <Button
                      outline
                      color="secondary"
                      active={sortKey === "lastName"}
                      onClick={() => toggleSort("lastName")}
                    >
                      Last name{sortIndicator("lastName")}
                    </Button>
                    <Button
                      outline
                      color="secondary"
                      active={sortKey === "assigned"}
                      onClick={() => toggleSort("assigned")}
                    >
                      Assigned{sortIndicator("assigned")}
                    </Button>
                    <Button
                      outline
                      color="secondary"
                      active={sortKey === "marked"}
                      onClick={() => toggleSort("marked")}
                    >
                      Marked{sortIndicator("marked")}
                    </Button>
                    <Button
                      outline
                      color="secondary"
                      active={sortKey === "pending"}
                      onClick={() => toggleSort("pending")}
                    >
                      Pending{sortIndicator("pending")}
                    </Button>
                  </ButtonGroup>
                </div>
                <div className="table-responsive">
                  <Table size="sm" className="align-items-center table-flush">
                    <thead className="thead-light">
                      <tr>
                        <th scope="col">Code</th>
                        <th scope="col">First name</th>
                        <th scope="col">Last name</th>
                        <th className="text-right" scope="col">
                          Assigned
                        </th>
                        <th className="text-right" scope="col">
                          Marked
                        </th>
                        <th className="text-right" scope="col">
                          Pending
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map((r, i) => (
                        <tr key={`${String(r.key)}-${i}`}>
                          <td className="text-monospace small">{r.code}</td>
                          <td>{r.firstName}</td>
                          <td>{r.lastName}</td>
                          <td className="text-right">{fmtNum(r.assigned)}</td>
                          <td className="text-right">{fmtNum(r.marked)}</td>
                          <td className="text-right">{fmtNum(r.pending)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
}
